import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { operations, selectors } from 'store/curriculumContentManager'
import { AppToContentGroupCode } from 'utils/routing/routeProductMaps'

/**
 * Hook that fetches content from the curriculum service POST /v1/content
 * endpoint. Replaces both useRouteGetter + useContentGetter in a single call.
 *
 * @param {Object} options
 * @param {string} options.app - Route-service app key (e.g. RouteContentProductAppMap.learnK5Product)
 * @param {string} options.routePath - URL path segment (e.g. '/elementary/teach')
 * @param {number} [options.siteId] - Active site id for audience scoping
 * @param {number} [options.contentGroupVersionId] - Optional specific CGV id
 * @param {boolean} [options.lazy] - If true, don't auto-fetch on mount
 */
export function useCurriculumContent({
  app,
  routePath,
  siteId,
  locale = 'en-US',
  contentGroupVersionId,
  lazy = false,
}) {
  const dispatch = useDispatch()

  const contentGroupCode = AppToContentGroupCode[app]
  // TODO(multi-site): Upcoming work will add multi-site fetching; revisit hasDeps,
  // cache keys, fetch, and selector so we support both a single active siteId and
  // multi-site flows without assuming scalar siteId only.
  const hasDeps =
    !!contentGroupCode && !!routePath && siteId !== null && siteId !== undefined

  // Fetch failures are stored on the cache entry and mapped to `error` by the
  // selector. Unexpected selection errors are also returned as `error` (see
  // selectCurriculumContent try/catch) so this hook does not throw on bad state.
  const selectorResult = useSelector(state => {
    if (!hasDeps) {
      return {
        route: null,
        page: null,
        coursePointer: null,
        error: null,
        loading: false,
      }
    }
    return selectors.selectCurriculumContent(
      contentGroupCode,
      routePath,
      siteId,
      locale,
    )(state)
  })

  const fetch = () => {
    if (!hasDeps) return
    dispatch(
      operations.fetchCurriculumContent({
        contentGroupCode,
        routePath,
        siteId,
        locale,
        contentGroupVersionId,
      }),
    )
  }

  useEffect(() => {
    if (lazy || !hasDeps) return
    if (selectorResult?.route || selectorResult?.error) return
    fetch()
  }, [contentGroupCode, routePath, siteId, locale, lazy])

  return {
    route: selectorResult.route,
    page: selectorResult.page,
    coursePointer: selectorResult.coursePointer,
    error: selectorResult.error,
    loading: selectorResult.loading,
    fetch,
    contentGroupCode,
  }
}
